import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CountryLabel } from "@/components/CountryLabel";
import { Globe, MapPin, Clock, CheckCircle, ArrowRight, Compass, Banknote, X } from "lucide-react";
import Footer from "@/components/Footer";
import { readAssessmentDraft } from "@/lib/assessmentDraft";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HOMEPAGE_ACTION_CONFIG = {
  plan: { label: "Continue Your Blueprint", href: "/plan" },
  compare: { label: "Compare Finalists", href: "/compare-countries" },
  results: { label: "View Your Results", href: "/assessment-results" },
  assessment: { label: "Continue Your Assessment", href: "/assessment" },
  start: { label: "Start Assessment", href: "/assessment" },
};

const SECONDARY_ACTION_PRIORITY = ["results", "compare", "plan", "assessment"];

const selectHomepageActions = ({ isAuthenticated, hasPlan, hasCompare, hasResults, hasPartialAssessment }) => {
  const validActions = {
    plan: Boolean(isAuthenticated && hasPlan),
    compare: Boolean(isAuthenticated && hasCompare),
    results: Boolean(isAuthenticated && hasResults),
    assessment: Boolean(isAuthenticated && hasPartialAssessment && !hasResults),
  };

  let primaryKey = "start";
  if (validActions.plan) {
    primaryKey = "plan";
  } else if (validActions.compare) {
    primaryKey = "compare";
  } else if (validActions.results) {
    primaryKey = "results";
  } else if (validActions.assessment) {
    primaryKey = "assessment";
  }

  const secondaryKeys = SECONDARY_ACTION_PRIORITY
    .filter((key) => key !== primaryKey && validActions[key])
    .slice(0, 2);

  return {
    primaryAction: { key: primaryKey, ...HOMEPAGE_ACTION_CONFIG[primaryKey] },
    secondaryActions: secondaryKeys.map((key) => ({ key, ...HOMEPAGE_ACTION_CONFIG[key] })),
    hasProgress: Boolean(validActions.plan || validActions.compare || validActions.results || validActions.assessment),
    isAuthenticated,
  };
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [resumeActions, setResumeActions] = useState(() => selectHomepageActions({
    isAuthenticated: false,
    hasPlan: false,
    hasCompare: false,
    hasResults: false,
    hasPartialAssessment: false,
  }));

  useEffect(() => {
    let isMounted = true;

    const loadHomepageState = async () => {
      try {
        const authResponse = await axios.get(`${API}/auth/status`, { withCredentials: true });
        if (!authResponse.data.authenticated) {
          if (isMounted) {
            setResumeActions(selectHomepageActions({
              isAuthenticated: false,
              hasPlan: false,
              hasCompare: false,
              hasResults: false,
              hasPartialAssessment: false,
            }));
          }
          return;
        }

        const draftExists = Boolean(readAssessmentDraft());
        const [dashboardResult, compareResult] = await Promise.allSettled([
          axios.get(`${API}/dashboard`, { withCredentials: true }),
          axios.get(`${API}/compare-countries`, { withCredentials: true }),
        ]);

        const dashboardData = dashboardResult.status === "fulfilled" ? dashboardResult.value.data : null;
        const compareData = compareResult.status === "fulfilled" ? compareResult.value.data : null;

        if (!isMounted) return;

        setResumeActions(selectHomepageActions({
          isAuthenticated: true,
          hasPlan: Boolean(dashboardData?.has_plan),
          hasCompare: Boolean(dashboardData?.intake_completed && Array.isArray(compareData?.countries) && compareData.countries.length > 1),
          hasResults: Boolean(dashboardData?.intake_completed),
          hasPartialAssessment: draftExists,
        }));
      } catch {
        if (isMounted) {
          setResumeActions(selectHomepageActions({
            isAuthenticated: false,
            hasPlan: false,
            hasCompare: false,
            hasResults: false,
            hasPartialAssessment: false,
          }));
        }
      }
    };

    loadHomepageState();
    return () => {
      isMounted = false;
    };
  }, []);

  const handlePrimaryCta = () => {
    setIsLoading(true);
    if (resumeActions.isAuthenticated) {
      navigate(resumeActions.primaryAction.href);
      return;
    }

    navigate("/auth");
  };

  const handleGetStarted = handlePrimaryCta;

  // Returning user login - goes to auth page with returning flag
  const handleLogin = () => {
    setIsLoading(true);
    navigate("/auth?returning=true");
  };

  const primaryCtaLabel = resumeActions.primaryAction.label;
  const heroPrimaryCtaLabel = resumeActions.isAuthenticated ? primaryCtaLabel : "Start Your Assessment";
  const heroHelperCopy = resumeActions.isAuthenticated && resumeActions.hasProgress
    ? "Pick up where you left off"
    : "✔ Free assessment • Takes about 3 minutes";

  const features = [
    {
      icon: <Compass className="w-6 h-6" />,
      title: "Lifestyle Fit",
      description: "See how well a country's culture, pace, and everyday life fit the way you want to live."
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Healthcare Quality",
      description: "Compare healthcare quality, access, and expected costs based on what matters most to you."
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Residency Path",
      description: "Understand which long-term residency options are the most realistic based on your profile and goals."
    },
    {
      icon: <Banknote className="w-6 h-6" />,
      title: "Cost of Living Fit",
      description: "See which countries realistically fit your monthly budget and long-term financial goals."
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Ease of Relocation",
      description: "Understand how easy—or difficult—it may be to move, settle, and adapt to life in each country."
    }
  ];

  const countries = [
    { name: "Argentina", code: "AR", highlight: "Lifestyle Fit", previewHref: "/country/argentina?entry=public" },
    { name: "Australia", code: "AU", highlight: "Infrastructure", previewHref: "/country/australia?entry=public" },
    { name: "Canada", code: "CA", highlight: "Ease of Relocation", previewHref: "/country/canada?entry=public" },
    { name: "Chile", code: "CL", highlight: "Stability", previewHref: "/country/chile?entry=public" },
    { name: "Colombia", code: "CO", highlight: "Community", previewHref: "/country/colombia?entry=public" },
    { name: "Costa Rica", code: "CR", highlight: "Lifestyle Fit", previewHref: "/country/costa-rica?entry=public" },
    { name: "Croatia", code: "HR", highlight: "Residency Path", previewHref: "/country/croatia?entry=public" },
    { name: "Ecuador", code: "EC", highlight: "Budget Fit", previewHref: "/country/ecuador?entry=public" },
    { name: "France", code: "FR", highlight: "Healthcare", previewHref: "/country/france?entry=public" },
    { name: "Germany", code: "DE", highlight: "Stability", previewHref: "/country/germany?entry=public" },
    { name: "Greece", code: "GR", highlight: "Lifestyle Fit", previewHref: "/country/greece?entry=public" },
    { name: "Italy", code: "IT", highlight: "Community", previewHref: "/country/italy?entry=public" },
    { name: "Japan", code: "JP", highlight: "Safety", previewHref: "/country/japan?entry=public" },
    { name: "Malaysia", code: "MY", highlight: "Residency Path", previewHref: "/country/malaysia?entry=public" },
    { name: "Mexico", code: "MX", highlight: "Budget Fit", previewHref: "/country/mexico?entry=public" },
    { name: "Netherlands", code: "NL", highlight: "Infrastructure", previewHref: "/country/netherlands?entry=public" },
    { name: "New Zealand", code: "NZ", highlight: "Lifestyle Fit", previewHref: "/country/new-zealand?entry=public" },
    { name: "Panama", code: "PA", highlight: "Ease of Relocation", previewHref: "/country/panama?entry=public" },
    { name: "Portugal", code: "PT", highlight: "Community", previewHref: "/country/portugal?entry=public" },
    { name: "Singapore", code: "SG", highlight: "Safety", previewHref: "/country/singapore?entry=public" },
    { name: "Spain", code: "ES", highlight: "Healthcare", previewHref: "/country/spain?entry=public" },
    { name: "Thailand", code: "TH", highlight: "Lifestyle Fit", previewHref: "/country/thailand?entry=public" },
    { name: "Uruguay", code: "UY", highlight: "Stability", previewHref: "/country/uruguay?entry=public" },
    { name: "Vietnam", code: "VN", highlight: "Budget Fit", previewHref: "/country/vietnam?entry=public" }
  ];

  const steps = [
    { step: "01", title: "Complete Your Assessment", description: "Tell us what matters most to you, including your budget, healthcare needs, lifestyle, and relocation priorities." },
    { step: "02", title: "Get Your Shortlist", description: "Discover your strongest country matches—and understand why each one fits your life." },
    { step: "03", title: "Unlock Your Blueprint", sublabel: "Optional • $29 one-time", description: "Explore your personalized Relocation Blueprint with city recommendations, residency pathways, budgeting insights, and practical next steps." }
  ];

  const earlyFeedback = [
    {
      quote: "For the first time, I had a structured way to compare countries instead of trying to piece everything together on my own. It helped me focus on the countries that genuinely fit my priorities.",
      attribution: "Valerie, CA - Retiree",
    },
    {
      quote: "Seeing Greece appear as one of my strongest matches confirmed what I had been thinking—but more importantly, it explained why. That made it much easier to include Greece on my shortlist.",
      attribution: "Joe, TX - Air Force veteran",
    },
  ];

  return (
    <div className="rc-page-shell min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b border-stone-800 bg-[rgba(12,10,9,0.96)] text-stone-50 shadow-[0_10px_30px_rgba(0,0,0,0.16)] backdrop-blur-md">
        <div className="rc-container">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3" data-testid="landing-header-home-link">
              <div className="flex items-center gap-2">
                <Globe className="w-7 h-7 text-amber-400" />
                <span className="font-manrope font-semibold text-xl tracking-tight text-stone-50">Relocation Clarity</span>
              </div>
              <span className="hidden md:inline text-sm font-normal text-stone-300/90">
                Independent Relocation Guidance
              </span>
            </Link>
            <div className="flex items-center gap-3">
              <Link
                to="/about"
                className="text-sm font-medium text-stone-300 transition-colors duration-200 hover:text-stone-50"
                data-testid="landing-header-about-link"
              >
                About
              </Link>
              <Link
                to="/insights"
                className="text-sm font-medium text-stone-300 transition-colors duration-200 hover:text-stone-50"
                data-testid="landing-header-insights-link"
              >
                Insights
              </Link>
              <Link
                to="/methodology"
                className="text-sm font-medium text-stone-300 transition-colors duration-200 hover:text-stone-50"
                data-testid="landing-header-faq-link"
              >
                FAQ
              </Link>
              <Link
                to="/contact"
                className="mr-4 text-sm font-medium text-stone-300 transition-colors duration-200 hover:text-stone-50 sm:mr-6"
                data-testid="landing-header-contact-link"
              >
                Contact
              </Link>
              {!resumeActions.isAuthenticated ? (
                <button 
                  data-testid="header-login-link"
                  onClick={handleLogin}
                  disabled={isLoading}
                  className="rounded-full border border-stone-700 bg-stone-900/80 px-4 py-2 text-sm font-medium text-stone-50 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] transition-[background-color,border-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:border-amber-400/45 hover:bg-stone-900 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_12px_24px_rgba(0,0,0,0.18)]"
                >
                  {isLoading ? "Loading..." : "Returning user? Log in"}
                </button>
              ) : null}
            </div>
          </div>
          <div className="md:hidden pb-2 -mt-1">
            <span className="text-xs text-stone-300/90 leading-tight">
              Independent Relocation Guidance
            </span>
          </div>
        </div>
      </header>

      <main>
        <section className="relative isolate min-h-[78vh] overflow-hidden border-b border-stone-200/80 bg-stone-200">
          <img
            src="https://customer-assets.emergentagent.com/job_c3de3b21-a894-4855-96dd-fa6113ff29ca/artifacts/4uqvd53i_Hero%202.jpg"
            alt="Passport, notebook, and planning notes on a table beside a window overlooking an everyday coastal neighborhood"
            className="absolute inset-0 h-full w-full object-cover object-[center_66%] md:object-[center_72%] brightness-[1.0] contrast-[1.02] saturate-[1.01]"
            data-testid="landing-hero-image"
          />
          <div className="absolute inset-0 bg-[rgba(246,242,236,0.05)]" />
          <div className="absolute inset-x-0 bottom-0 h-28 bg-[linear-gradient(180deg,rgba(246,242,236,0)_0%,rgba(246,242,236,0.05)_60%,rgba(246,242,236,0.11)_100%)]" />
          <div className="rc-container relative flex min-h-[78vh] items-end py-10 sm:py-14 lg:py-20">
            <div className="max-w-3xl">
              <div className="max-w-2xl space-y-7 rounded-[2rem] bg-[linear-gradient(135deg,rgba(253,252,248,0.90),rgba(253,252,248,0.72))] p-6 shadow-[0_12px_24px_rgba(28,25,23,0.05)] backdrop-blur-[2px] sm:p-7" data-testid="landing-hero-panel">
                <h1 className="mb-5 max-w-[22ch] font-display text-[1.9rem] leading-[0.98] tracking-[-0.03em] text-stone-900 sm:max-w-[23ch] sm:text-[2.2rem] lg:max-w-[23ch] lg:text-[2.75rem]" data-testid="landing-hero-headline">
                  <span className="block">
                    <span className="block">Stop guessing where</span>
                    <span className="block">to live abroad.</span>
                  </span>
                </h1>
                <p className="max-w-xl text-lg font-medium leading-8 text-stone-900 sm:text-[1.35rem] sm:leading-9" data-testid="landing-hero-subheadline">
                  Find the country that&apos;s right for your life.
                </p>
                <p className="max-w-2xl text-base leading-8 text-stone-800 sm:text-lg">
                  Most relocation advice tells you what a country is like. Relocation Clarity helps you decide whether it fits your budget, healthcare needs, visa and residency options, lifestyle, personal priorities, and long-term goals—so you can make one of life&apos;s biggest decisions with confidence.
                </p>
                <div className="!mt-9 flex flex-col items-start gap-4 sm:flex-row">
                  <div className="flex flex-col items-start" data-testid="hero-primary-cta-group">
                    <Button 
                      data-testid="hero-get-started-btn"
                      onClick={handlePrimaryCta}
                      disabled={isLoading}
                      size="lg"
                      className="group h-12 rounded-full bg-stone-950 px-8 text-base font-medium text-stone-50 shadow-[0_16px_34px_rgba(28,25,23,0.22)] transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-1 hover:bg-black hover:shadow-[0_24px_44px_rgba(28,25,23,0.28)]"
                    >
                      {isLoading ? "Loading..." : heroPrimaryCtaLabel}
                      <ArrowRight className="ml-2 h-5 w-5 transition-colors duration-200 group-hover:text-amber-400" />
                    </Button>
                    <p className="mt-2 pl-2 text-xs font-medium tracking-[0.01em] text-stone-600/90" data-testid="hero-get-started-helper-copy">
                      {heroHelperCopy}
                    </p>
                    {resumeActions.isAuthenticated && resumeActions.secondaryActions.length ? (
                      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 pl-2" data-testid="hero-secondary-resume-links">
                        {resumeActions.secondaryActions.map((action) => (
                          <Link
                            key={action.key}
                            to={action.href}
                            className="text-sm font-medium text-stone-600 underline-offset-4 transition-colors duration-200 hover:text-stone-900 hover:underline"
                            data-testid={`hero-secondary-resume-link-${action.key}`}
                          >
                            {action.label}
                          </Link>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <Button
                    asChild
                    data-testid="hero-sample-plan-btn"
                    variant="outline"
                    size="lg"
                    className="group h-12 rounded-full border border-white/58 bg-[rgba(255,255,255,0.24)] px-8 text-base font-medium text-stone-900 shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_12px_28px_rgba(28,25,23,0.07)] backdrop-blur-md transition-[background-color,border-color,color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:border-white/78 hover:bg-[rgba(255,255,255,0.30)] hover:text-stone-700 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.42),0_14px_30px_rgba(28,25,23,0.09)]"
                  >
                    <Link to="/sample-plan" className="text-inherit group-hover:text-stone-700">Preview a Sample Blueprint</Link>
                  </Button>
                </div>
                <p className="!mt-7 max-w-[39rem] text-sm leading-7 text-stone-700/92 sm:text-[0.96rem] sm:leading-7" data-testid="landing-hero-trust-statement">
                  Relocation Clarity isn&apos;t influenced by destination sponsorships or marketing. We don&apos;t promote countries, sell real estate, or offer relocation seminars. <strong className="font-semibold text-stone-900">We exist to help you make the right relocation decision.</strong>
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="rc-home-section-tight">
          <div className="rc-container">
            <div className="mx-auto max-w-4xl border-t border-stone-200/80 pt-10 text-center" data-testid="decision-support-card">
              <div className="rc-home-intro-centered mb-0">
                <p className="rc-section-kicker mb-4 text-center">LOOK BEYOND THE RANKINGS</p>
                <h2 className="rc-section-title mb-4 text-center" data-testid="decision-support-title">
                  Compare countries through the lens of your life.
                </h2>
                <div className="mx-auto max-w-[42rem] space-y-5 text-center text-base leading-8 text-stone-600" data-testid="decision-support-description">
                  <p>Country rankings can only tell you so much.</p>
                  <p>We evaluate each country based on your budget, healthcare needs, residency options, and personal priorities.</p>
                  <p><strong className="font-semibold text-stone-900">Because choosing where to live isn&apos;t about finding the best country.<br className="hidden sm:block" /> It&apos;s about finding the right one for your life.</strong></p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="rc-home-section">
          <div className="rc-container">
            <div className="rc-home-intro">
              <p className="rc-section-kicker mb-3">What You Get</p>
              <h2 className="rc-section-title mb-4">
                See which countries fit — before you commit.
              </h2>
              <p className="text-base leading-8 text-stone-600">
                Relocation Clarity helps you narrow your shortlist with greater confidence by comparing the factors that matter most before you commit to a move abroad.
              </p>
            </div>
            <div className="rc-home-feature-grid">
              {features.map((feature, index) => (
                <Card 
                  key={feature.title}
                  className={`rc-subtle-card border-stone-200/80 ${index < 3 ? "xl:col-span-4" : "xl:col-span-6"}`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <CardHeader className="space-y-5 p-8">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full border border-amber-200 bg-amber-50 text-amber-400">
                      {feature.icon}
                    </div>
                    <CardTitle className="font-manrope text-2xl text-stone-900">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="px-8 pb-8 pt-0">
                    <CardDescription className="text-base leading-7 text-stone-600">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section id="how-it-works" className="rc-home-section">
          <div className="rc-container">
            <div className="rc-home-panel-bleed text-left">
              <div className="rc-home-panel-inner text-left">
              <div className="rc-home-intro-compact">
                  <p className="rc-section-kicker mb-3">How It Works</p>
                  <h2 className="rc-section-title mb-4 text-left">A better way to decide.</h2>
                  <p className="text-left text-base leading-8 text-stone-600">
                    Complete a short assessment, review your best-fit countries, and understand why they match your life before deciding.
                  </p>
                </div>
                <div className="rc-home-process-grid">
                  {steps.map((item, index) => (
                    <div key={item.step} className="rounded-[1.6rem] border border-stone-200 bg-stone-50/70 p-6">
                      <div className="mb-6 flex items-center justify-between">
                        <span className="font-manrope text-3xl text-stone-900">{item.step}</span>
                        {index < steps.length - 1 ? <ArrowRight className="h-4 w-4 text-amber-400" /> : null}
                      </div>
                      <h3 className="font-manrope text-2xl text-stone-900 mb-3">{item.title}</h3>
                      {item.sublabel ? (
                        <p className="-mt-1 mb-3 text-xs font-medium tracking-[0.14em] text-stone-500">{item.sublabel}</p>
                      ) : null}
                      <p className="text-sm leading-7 text-stone-600">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="rc-home-section" id="country-previews">
          <div className="rc-container">
            <div className="rc-home-panel-bleed text-left">
              <div className="rc-home-panel-inner text-left">
              <div className="rc-home-intro">
                  <p className="rc-section-kicker mb-3">COUNTRY PREVIEWS</p>
                  <h2 className="rc-section-title mb-4 text-left">Explore Country Profiles</h2>
                  <p className="text-left text-sm leading-7 text-stone-600">
                    Explore practical country profiles that highlight lifestyle, healthcare, cost of living, residency, and other factors that matter when deciding where to live abroad.
                  </p>
                </div>
                <div className="rc-home-chip-group">
                  {countries.map((country) => {
                    const content = (
                      <>
                        <p className="font-medium text-stone-900 text-sm">
                          <CountryLabel code={country.code} name={country.name} size={15} flagTestId={`landing-country-flag-${country.code}`} />
                        </p>
                        <p className="mt-1 text-xs text-stone-500" data-testid={`landing-country-tag-${country.code}`}>{country.highlight}</p>
                      </>
                    );

                    if (country.previewHref) {
                      return (
                        <Link
                          key={country.name}
                          to={country.previewHref}
                          className="rounded-full border border-stone-200 bg-stone-50/80 px-4 py-3 transition-[border-color,background-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:border-stone-300 hover:bg-white hover:shadow-[0_10px_24px_rgba(28,25,23,0.06)]"
                          data-testid={`landing-country-link-${country.code}`}
                        >
                          {content}
                        </Link>
                      );
                    }

                    return (
                      <div 
                        key={country.name}
                        className="rounded-full border border-stone-200 bg-stone-50/80 px-4 py-3"
                      >
                        {content}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="rc-home-section pt-0" data-testid="homepage-early-feedback-section">
          <div className="rc-container">
            <div className="max-w-3xl space-y-4 text-left" data-testid="homepage-early-feedback-intro">
              <p className="rc-section-kicker mb-3">USER PERSPECTIVES</p>
              <h2 className="rc-section-title text-left">Real perspectives. Better decisions.</h2>
            </div>

            <div className="mt-8 grid gap-5 lg:grid-cols-2" data-testid="homepage-early-feedback-grid">
              {earlyFeedback.map((item, index) => (
                <Card key={item.quote} className="rc-subtle-card border-stone-200/85 bg-white/92" data-testid={`homepage-early-feedback-card-${index + 1}`}>
                  <CardContent className="p-7 sm:p-8">
                    <div className="mb-5 h-1.5 w-12 rounded-full bg-amber-400/80" data-testid={`homepage-early-feedback-accent-${index + 1}`} />
                    <blockquote className="text-[1.05rem] leading-8 text-stone-800" data-testid={`homepage-early-feedback-quote-${index + 1}`}>
                      “{item.quote}”
                    </blockquote>
                    <p className="mt-5 text-sm leading-7 text-stone-500" data-testid={`homepage-early-feedback-attribution-${index + 1}`}>
                      — {item.attribution}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="rc-home-section-ending">
          <div className="rc-container">
            <div className="rc-home-pricing-grid">
              <div className="rc-home-intro pt-2 lg:mb-0 lg:pr-6">
                <p className="rc-section-kicker mb-3">Pricing</p>
                <h2 className="rc-section-title mb-4">
                  Ready to Decide?
                </h2>
                <p className="text-base leading-8 text-stone-600">
                  Start with a free assessment to discover your strongest country matches. When you&apos;re ready to understand your results more fully, unlock your personalized Relocation Blueprint with tailored recommendations, practical planning tools, and clear explanations of why each country fits your priorities.
                </p>
                <div className="mt-8 max-w-xl space-y-4 text-sm leading-7 text-stone-500" data-testid="pricing-trust-statement">
                  <p>The assessment is free. Upgrade only if you want deeper guidance.</p>
                  <p>One-time purchase. No subscriptions. No sponsored destinations. Just independent guidance focused on helping you make the right relocation decision.</p>
                </div>
              </div>
              <div className="rc-home-pricing-cards">
                <Card className="rc-subtle-card border-stone-200/80">
                  <CardHeader className="p-8">
                    <CardTitle className="font-manrope text-2xl text-stone-900">Free Assessment</CardTitle>
                    <CardDescription className="text-base leading-7 text-stone-600">Narrow your shortlist with confidence</CardDescription>
                    <div className="pt-6">
                      <span className="font-manrope text-5xl text-stone-900">$0</span>
                    </div>
                  </CardHeader>
                  <CardContent className="px-8 pb-8 pt-0">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Included</p>
                    <ul className="mt-4 space-y-3">
                      {[
                        "Guided Relocation Assessment",
                        "Discover Your Strongest Country Matches",
                        "Understand Why Each Country Fits",
                        "Key Tradeoffs to Consider",
                        "Personalized Fit Scores",
                        "Budget & Lifestyle Snapshot"
                      ].map((item) => (
                        <li key={item} className="flex items-start gap-3">
                          <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-700" />
                          <span className="text-stone-600 leading-7">{item}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-6 border-t border-stone-200 pt-6">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Not included</p>
                      <ul className="mt-4 space-y-3">
                        {[
                          "Region and city guidance",
                          "Detailed tradeoff analysis",
                          "Residency pathway breakdowns",
                          "Side-by-side comparison matrix",
                          "Personalized relocation timeline"
                        ].map((item) => (
                          <li key={item} className="flex items-start gap-3">
                            <X className="mt-0.5 h-5 w-5 flex-shrink-0 text-rose-500" />
                            <span className="text-stone-500 leading-7">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <Button 
                      data-testid="free-plan-btn"
                      onClick={handleGetStarted}
                      disabled={isLoading}
                      variant="outline"
                      className="rc-button-outline mt-8 w-full"
                    >
                      Start Free Assessment
                    </Button>
                  </CardContent>
                </Card>

                <Card className="rc-subtle-card border-amber-300/70 bg-amber-50/55 relative overflow-hidden">
                  <div className="absolute left-8 right-8 top-0 h-1 rounded-full bg-amber-500" />
                  <CardHeader className="p-8">
                    <CardTitle className="font-manrope text-2xl text-stone-900">Personalized Relocation Blueprint</CardTitle>
                    <CardDescription className="text-base leading-7 text-stone-600">Turn your shortlist into a clearer path forward</CardDescription>
                    <div className="pt-6">
                      <span className="font-manrope text-5xl text-stone-900">$29</span>
                      <span className="ml-2 text-stone-500">one-time</span>
                    </div>
                  </CardHeader>
                  <CardContent className="px-8 pb-8 pt-0">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Included</p>
                    <ul className="mt-4 space-y-3">
                      {[
                        "Everything included in Free, plus:",
                        "Personalized recommendations you can act on",
                        "City and region guidance to narrow the shortlist",
                        "Residency pathway guidance grounded in your situation",
                        "Budget planning with clearer real-world tradeoffs",
                        "Side-by-side comparison tools for your finalists",
                        "A practical relocation roadmap with next steps",
                        "One included plan refresh if your priorities later change",
                      ].map((item) => (
                        <li key={item} className="flex items-start gap-3">
                          <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-700" />
                          <span className="text-stone-700 leading-7">{item}</span>
                        </li>
                      ))}
                    </ul>
                    <Button 
                      data-testid="paid-plan-btn"
                      onClick={handleGetStarted}
                      disabled={isLoading}
                      className="rc-button-accent mt-8 w-full"
                    >
                      Unlock Your Relocation Blueprint
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default LandingPage;
